import {useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {Button, DataTable, IconButton, Title} from 'react-native-paper';
import {useMutation, useQueryClient} from 'react-query';
import firestore from '@react-native-firebase/firestore';

import {ReservationYear, useReservationYears} from '../hooks/useDomainData';
import {ReservationYearEditor} from '../components/FormikStoreEditors/ReservationYearEditor';
import {useAppSettings} from '../components/AppSettings';
import {useAuthPersonInfo} from '../hooks/usePersonInfo';

function ReservationYears() {
  const person = useAuthPersonInfo();
  const queryClient = useQueryClient();
  const appSettings = useAppSettings();
  const reservationYears = useReservationYears();

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [currentFirestoreObject, setCurrentFirestoreObject] = useState<
    ReservationYear | undefined
  >();

  const collectionName = 'reservationYears';
  const handleDeleteFirestoreObject = useMutation(
    (firestoreObject: ReservationYear) => {
      console.log('should delete: ' + firestoreObject.ref.path);
      return firestore()
        .collection(collectionName)
        .doc(firestoreObject.ref.id)
        .delete();
    },
    {
      onSettled: () => {
        console.log('deleting succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
    },
  );

  const handleEditFirestoreObject = (firestoreObject?: ReservationYear) => {
    console.log(
      'open for edit or new object: ' +
        JSON.stringify(firestoreObject, null, 2),
    );
    setCurrentFirestoreObject(firestoreObject);
    setEditDialogVisible(true);
  };

  if (!person || (!person.isAdmin && !person.isGuestRelations)) {
    return null;
  }

  return (
    <>
      <View style={styles.horizontalCentered}>
        <Title style={styles.title}>Reservation Years</Title>
        {(person?.isAdmin || person?.isGuestRelations) && (
          <Button mode="outlined" onPress={() => handleEditFirestoreObject()}>
            Create Reservation Year
          </Button>
        )}
      </View>
      <ScrollView
        persistentScrollbar={true}
        style={[styles.container, styles.content]}>
        <DataTable>
          {!reservationYears && (
            <DataTable.Row>
              <DataTable.Header>
                <DataTable.Title>loading...</DataTable.Title>
              </DataTable.Header>
            </DataTable.Row>
          )}
          {reservationYears && (
            <DataTable.Header style={styles.shortHeader}>
              <DataTable.Title style={styles.borderCell}>ID</DataTable.Title>
              <DataTable.Title style={styles.widerCell}>Name</DataTable.Title>
              <DataTable.Title style={styles.borderCell}>
                Start Date
              </DataTable.Title>
              <DataTable.Title style={styles.borderCell}>
                End Date
              </DataTable.Title>
              <DataTable.Title style={styles.borderCell}>
                Actions
              </DataTable.Title>
            </DataTable.Header>
          )}
          {reservationYears && reservationYears.length === 0 && (
            <DataTable.Row>
              <DataTable.Cell>No reservation years found.</DataTable.Cell>
            </DataTable.Row>
          )}
          {reservationYears &&
            reservationYears.length > 0 &&
            reservationYears.map(reservationYear => (
              <DataTable.Row key={reservationYear.reservationYearId}>
                <DataTable.Cell style={styles.borderCell}>
                  {reservationYear.reservationYearId}
                </DataTable.Cell>
                <DataTable.Cell style={styles.widerCell}>
                  {reservationYear.name}
                </DataTable.Cell>
                <DataTable.Cell style={styles.borderCell}>
                  {reservationYear.startDate.toDate().toUTCString()}
                </DataTable.Cell>
                <DataTable.Cell style={styles.borderCell}>
                  {reservationYear.endDate.toDate().toUTCString()}
                </DataTable.Cell>
                <DataTable.Cell style={styles.borderCell}>
                  <>
                    <IconButton
                      icon="pencil"
                      size={18}
                      color={appSettings.currentTheme.colors.primary}
                      onPress={() => handleEditFirestoreObject(reservationYear)}
                    />
                    {person?.isAdmin && (
                      <IconButton
                        icon="delete"
                        size={18}
                        color={appSettings.currentTheme.colors.primary}
                        onPress={() =>
                          handleDeleteFirestoreObject.mutate(reservationYear)
                        }
                      />
                    )}
                  </>
                </DataTable.Cell>
              </DataTable.Row>
            ))}
        </DataTable>
      </ScrollView>
      <ReservationYearEditor
        dialogVisible={editDialogVisible}
        setDialogVisible={(visible: boolean) => {
          setEditDialogVisible(visible);
          if (!visible) {
            setCurrentFirestoreObject(undefined);
          }
        }}
        reservationYear={currentFirestoreObject}
      />
    </>
  );
}

const styles = StyleSheet.create({
  widerCell: {
    minWidth: 150,
    width: 100,
    justifyContent: 'center',
    alignItems: 'center',
  },
  borderCell: {
    borderStartWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    position: 'relative',
  },
  content: {
    paddingHorizontal: 20,
  },
  center: {
    width: '100%',
    alignItems: 'center',
  },
  shortHeader: {
    height: 25,
  },
  title: {
    padding: 10,
    paddingHorizontal: 20,
  },
  horizontalCentered: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
    paddingHorizontal: 10,
  },
});

export default ReservationYears;
